import { Link } from 'react-router-dom';
import { ServiceOption } from './ServiceStep';
import { VehicleType } from './VehicleStep';

interface ResultPanelProps {
  service: ServiceOption;
  vehicle: VehicleType;
  ageMultiplier: number;
  ageLabel: string;
  onReset: () => void;
}

const ResultPanel = ({ service, vehicle, ageMultiplier, ageLabel, onReset }: ResultPanelProps) => {
  const totalMult = service.fixedPrice ? 1 : vehicle.multiplier * ageMultiplier;
  const priceMin = Math.round((service.baseMin * totalMult) / 5) * 5;
  const priceMax = Math.round((service.baseMax * totalMult) / 5) * 5;

  const whatsappMsg = encodeURIComponent(
    `Hola! Estoy interesado en un presupuesto para "${service.label}" para mi ${vehicle.label} (${ageLabel}). ¿Me podéis dar más información?`,
  );
  const whatsappUrl = `https://wa.me/34666365922?text=${whatsappMsg}`;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 px-4 py-2 mb-6">
          <i className="ri-sparkling-line text-yellow-400 text-sm" />
          <span className="text-yellow-400 text-xs font-bold uppercase tracking-widest">Estimación lista</span>
        </div>
        <h3 className="text-white text-2xl md:text-3xl font-bold mb-2">
          {priceMin} – {priceMax} <span className="text-yellow-400">€</span>
        </h3>
        <p className="text-zinc-500 text-sm">Precio orientativo para <strong className="text-zinc-300">{service.label}</strong></p>
      </div>

      <div className="bg-zinc-900/60 border border-zinc-800 p-6 mb-6 space-y-4">
        <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-4">Resumen de tu solicitud</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-zinc-800/50 p-3 text-center">
            <div className="w-9 h-9 flex items-center justify-center bg-yellow-400/10 mx-auto mb-2">
              <i className={`${service.icon} text-yellow-400 text-sm`} />
            </div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Servicio</p>
            <p className="text-white text-xs font-bold">{service.label}</p>
          </div>
          <div className="bg-zinc-800/50 p-3 text-center">
            <div className="w-9 h-9 flex items-center justify-center bg-yellow-400/10 mx-auto mb-2">
              <i className={`${vehicle.icon} text-yellow-400 text-sm`} />
            </div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Vehículo</p>
            <p className="text-white text-xs font-bold">{vehicle.label}</p>
          </div>
          <div className="bg-zinc-800/50 p-3 text-center">
            <div className="w-9 h-9 flex items-center justify-center bg-yellow-400/10 mx-auto mb-2">
              <i className="ri-calendar-line text-yellow-400 text-sm" />
            </div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Antigüedad</p>
            <p className="text-white text-xs font-bold">{ageLabel}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-information-line text-zinc-600 text-sm flex-shrink-0" />
            <p className="text-zinc-600 text-[11px] leading-relaxed">
              Esta estimación es orientativa. El precio final puede variar según el estado real del vehículo y las piezas necesarias. El presupuesto exacto y gratuito se entrega en taller.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-line text-yellow-400/50 text-sm flex-shrink-0" />
            <p className="text-zinc-500 text-[11px]">
              Siempre te informamos del precio antes de empezar cualquier trabajo. Sin sorpresas.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex-1 flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1ebe5a] text-white text-xs font-bold uppercase tracking-widest px-6 py-3.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-base" />
          Pedir presupuesto por WhatsApp
        </a>
        <Link
          to="/contacto"
          className="flex-1 flex items-center justify-center gap-2 border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black text-xs font-bold uppercase tracking-widest px-6 py-3.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-mail-line" />
          Contactar por formulario
        </Link>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-300 text-xs uppercase tracking-widest transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          Calcular otro servicio
        </button>
      </div>
    </div>
  );
};

export default ResultPanel;
